import React from "react";
import {
  getReasonLabel,
  getDetectionTypeBadge,
  getProposalLabel,
} from "./inscriptionHelpers";
import { DateTime } from "luxon";

const InscriptionCardDecision = ({ inscription, hasDecision }) => {
  const decisionDiffersFromProposal =
    hasDecision &&
    (inscription.decisionId !== inscription.selectedProposal ||
      inscription.decisionPercent !== inscription.proposalPercent ||
      inscription.decisionDuration !== inscription.proposalDuration);

  const decisionIsIncreased =
    hasDecision &&
    ((inscription.decisionPercent !== undefined &&
      inscription.proposalPercent !== undefined &&
      inscription.decisionPercent > inscription.proposalPercent) ||
      (inscription.decisionDuration !== undefined &&
        inscription.proposalDuration !== undefined &&
        inscription.decisionDuration > inscription.proposalDuration));

  // Les radiations complémentaires ne se voient que cochées : la liste doit
  // les signaler sans rouvrir la décision.
  const radiations = [
    inscription.radiationBRSA && {
      id: "brsa",
      label: "Radiation BRSA",
      icon: "fa-user-times",
      title: "Radiation de la liste des BRSA, sauf si mise en conformité",
    },
    inscription.radiationDE && {
      id: "de",
      label: "Radiation DE",
      icon: "fa-briefcase",
      title: "Radiation de la liste des demandeurs d'emploi",
    },
  ].filter(Boolean);

  return (
    <div className="d-flex flex-column h-100" style={{ gap: 8 }}>
      {/* Proposition RU */}
      <div
        className="p-2 rounded"
        style={{
          background: "linear-gradient(135deg, #eff6ff, #dbeafe)",
          border: "1px solid #bfdbfe",
        }}
      >
        <div className="d-flex align-items-center mb-1" style={{ gap: 6 }}>
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              background: "#3b82f6",
            }}
          >
            <i className="fa fa-user" style={{ fontSize: 10, color: "#fff" }} />
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#1e40af",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Proposition RU
          </span>
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#1e293b",
            fontWeight: 600,
          }}
        >
          {inscription.proposalPercent !== undefined &&
          inscription.proposalDuration !== undefined
            ? `Réduction ${inscription.proposalPercent}% — ${inscription.proposalDuration} mois`
            : getProposalLabel(inscription.selectedProposal)}
        </div>
      </div>

      {/* Décision EP */}
      {hasDecision ? (
        <div
          className="p-2 rounded"
          style={{
            background: decisionDiffersFromProposal
              ? "linear-gradient(135deg, #fefce8, #fef9c3)"
              : "linear-gradient(135deg, #f0fdf4, #dcfce7)",
            border: decisionDiffersFromProposal
              ? "1px solid #fde68a"
              : "1px solid #86efac",
          }}
        >
          <div className="d-flex align-items-center mb-1" style={{ gap: 6 }}>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 20,
                height: 20,
                borderRadius: 4,
                background: decisionDiffersFromProposal ? "#f59e0b" : "#22c55e",
              }}
            >
              <i
                className={`fa ${decisionDiffersFromProposal ? "fa-exchange" : "fa-check"}`}
                style={{ fontSize: 10, color: "#fff" }}
              />
            </div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: decisionDiffersFromProposal ? "#92400e" : "#166534",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Décision EP
            </span>
            {decisionIsIncreased && (
              <span
                className="d-inline-flex align-items-center px-1 rounded"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#ed3a3a",
                  background: "#fee9e9",
                  gap: 3,
                }}
                title="Le barème de la décision est supérieur à la proposition du RU"
              >
                <i className="fa fa-arrow-up" style={{ fontSize: 9 }} />
                Augmentée
              </span>
            )}
            {decisionDiffersFromProposal && !decisionIsIncreased && (
              <span
                className="d-inline-flex align-items-center px-1 rounded"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#92400e",
                  background: "#fef3c7",
                  gap: 3,
                }}
                title="La décision diffère de la proposition du RU"
              >
                <i
                  className="fa fa-exclamation-triangle"
                  style={{ fontSize: 9 }}
                />
                Modifiée
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#1e293b",
              fontWeight: 600,
            }}
          >
            {inscription.decisionText || "Décision enregistrée"}
          </div>
          {radiations.length > 0 && (
            <div className="d-flex flex-wrap mt-1" style={{ gap: 4 }}>
              {radiations.map((r) => (
                <span
                  key={r.id}
                  className="inscription-card__radiation d-inline-flex align-items-center px-1 rounded"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#991b1b",
                    background: "#fee2e2",
                    gap: 3,
                  }}
                  title={r.title}
                >
                  <i className={`fa ${r.icon}`} style={{ fontSize: 9 }} />
                  {r.label}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div
          className="p-2 rounded d-flex align-items-center"
          style={{
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            gap: 8,
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              background: "#e2e8f0",
            }}
          >
            <i
              className="fa fa-clock-o"
              style={{ fontSize: 10, color: "#94a3b8" }}
            />
          </div>
          <span
            style={{
              fontSize: 12,
              color: "#94a3b8",
              fontStyle: "italic",
            }}
          >
            En attente de décision
          </span>
        </div>
      )}

      {/* Antécédents EP */}
      {inscription.epHistory && inscription.epHistory.length > 0 ? (
        <div
          className="px-2 py-1 rounded"
          style={{
            background: "#fffbeb",
            border: "1px solid #fde68a",
          }}
        >
          <div className="d-flex align-items-center mb-1" style={{ gap: 5 }}>
            <i
              className="fa fa-history"
              style={{ fontSize: 10, color: "#92400e" }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#92400e",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {inscription.epHistory.length} antécédent(s) EP
            </span>
          </div>
          {inscription.epHistory.map((ant, idx) => {
            const antDetection = getDetectionTypeBadge(ant.detectionType);
            return (
              <div
                key={idx}
                className="d-flex flex-column"
                style={{
                  fontSize: 11,
                  color: "#78350f",
                  marginTop: idx > 0 ? 4 : 2,
                  paddingTop: idx > 0 ? 4 : 0,
                  borderTop: idx > 0 ? "1px solid #fde68a" : "none",
                }}
              >
                <div
                  className="d-flex align-items-center flex-wrap"
                  style={{ gap: 4 }}
                >
                  <span style={{ fontWeight: 700 }}>
                    EP du {DateTime.fromISO(ant.epDate).toFormat("dd/MM/yyyy")}
                  </span>
                  {ant.notificationDate && (
                    <span style={{ fontWeight: 400, color: "#a16207" }}>
                      — décision le{" "}
                      {DateTime.fromISO(ant.notificationDate).toFormat(
                        "dd/MM/yyyy",
                      )}
                    </span>
                  )}
                  {antDetection && (
                    <span
                      className="px-1 rounded"
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        background: antDetection.bg,
                        color: antDetection.color,
                      }}
                    >
                      {antDetection.label}
                    </span>
                  )}
                </div>
                <span>
                  {ant.type && <>{ant.type} — </>}
                  {ant.mainReason && <>{getReasonLabel(ant.mainReason)} — </>}
                  {ant.label}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className="px-2 py-1 rounded d-flex align-items-center"
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            gap: 5,
          }}
        >
          <i
            className="fa fa-check-circle"
            style={{ fontSize: 10, color: "#22c55e" }}
          />
          <span
            style={{
              fontSize: 11,
              color: "#166534",
              fontWeight: 500,
            }}
          >
            Aucun antécédent EP
          </span>
        </div>
      )}
    </div>
  );
};

export default InscriptionCardDecision;
